import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  createScimCredential,
  disconnectScimProvisioning,
  getScimProvisioning,
} from '@kirin/api-client/api/scim-provisioning';
import type {
  CreateScimCredentialRequest,
  CreateScimCredentialResponse,
  DisconnectScimProvisioningResponse,
  ScimProvisioningConfig,
} from '@kirin/api-client/contracts/scim-provisioning';
import { apiClient } from '@/lib/api-client';
import { trackEvent } from '@/lib/analytics';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';

/**
 * React Query bindings for organization SCIM provisioning (KIR-4693).
 *
 * **The one-time bearer never enters the query cache.** `generate` returns it
 * through `mutateAsync` and nothing here writes it back with `setQueryData`, so
 * it lives only in the calling component's state.
 */

/**
 * The SCIM provisioning view (URL + credential). `retryHandlers.detail` and
 * callers pass `enabled: false` until the role gate resolves. No feature flag —
 * an unconfigured environment returns an empty `scimUrl`, not a 404.
 */
export function useScimProvisioning(
  organizationId: string,
  enabled = true
): UseQueryResult<ScimProvisioningConfig, unknown> {
  return useQuery({
    queryKey: queryKeys.organizations.scimProvisioning(organizationId),
    queryFn: () => getScimProvisioning(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    retry: retryHandlers.detail,
  });
}

/**
 * Generate a SCIM credential. Use `mutateAsync` and hold the returned
 * `bearerToken` in component state — it is the only time it is available.
 */
export function useCreateScimCredential(
  organizationId: string
): UseMutationResult<
  CreateScimCredentialResponse,
  unknown,
  CreateScimCredentialRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateScimCredentialRequest) =>
      createScimCredential(apiClient, organizationId, body),
    onSuccess: () => {
      trackEvent('scim_credential_created', {
        organization_id: organizationId,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.scimProvisioning(organizationId),
      });
    },
  });
}

/** Disconnect SCIM provisioning; invalidates the view so its status refreshes. */
export function useDisconnectScimProvisioning(
  organizationId: string
): UseMutationResult<DisconnectScimProvisioningResponse, unknown, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => disconnectScimProvisioning(apiClient, organizationId),
    onSuccess: () => {
      trackEvent('scim_provisioning_disconnected', {
        organization_id: organizationId,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.scimProvisioning(organizationId),
      });
    },
  });
}
